import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  Camera,
  Check,
  Download,
  FileText,
  FolderArchive,
  MapPin,
  MapPinOff,
  MonitorX,
  RotateCcw,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { resultFilesApi } from "../api/resultFiles";
import { useContractorPreview } from "../contractorPreview";
import {
  useAcceptResultFile,
  useDeleteResultFile,
  useOrderResultFiles,
  useRejectResultFile,
  useUploadResultFiles,
} from "../hooks/useResultFiles";
import type { OrderResultFile, OrderResultFileStatus } from "../types";

function fmtSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

const STATUS_META: Record<OrderResultFileStatus, { label: string; cls: string }> = {
  pending: {
    label: "Oczekuje",
    cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  },
  accepted: {
    label: "Zaakceptowany",
    cls: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  },
  rejected: {
    label: "Odrzucony",
    cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  },
};

function StatusBadge({ status }: { status: OrderResultFileStatus }) {
  const m = STATUS_META[status];
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
}

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

const CHIP = "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium";
const CHIP_WARN = `${CHIP} bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300`;
const CHIP_BAD = `${CHIP} bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300`;
const CHIP_OK = `${CHIP} bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300`;
const CHIP_MUTED = `${CHIP} bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300`;

/**
 * Photo-check chips under a file row (EXIF read at upload — see apps/orders/photo_meta.py):
 * geotag present (link to the map) / missing (warning, stronger when the position requires
 * it), screenshot auto-rejected, "do weryfikacji" for a single suspicious sign, and the
 * "odrzucone automatycznie, przywrócone przez X" trace. Non-images render nothing.
 */
function PhotoChips({ f, geotagRequired }: { f: OrderResultFile; geotagRequired: boolean }) {
  if (!f.is_image) return null;
  const reasons = f.photo_check_reasons?.length ? f.photo_check_reasons.join("; ") : "";
  const camera = [f.camera_make, f.camera_model].filter(Boolean).join(" ");
  const details = [
    f.taken_at ? `wykonano ${fmtDateTime(f.taken_at)}` : null,
    camera || null,
    f.image_width && f.image_height ? `${f.image_width}×${f.image_height} px` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {f.has_geotag && f.gps_lat != null && f.gps_lng != null ? (
        <a
          className={`${CHIP_OK} hover:underline`}
          href={`https://www.google.com/maps?q=${f.gps_lat},${f.gps_lng}`}
          target="_blank"
          rel="noreferrer"
          title={`Współrzędne z EXIF: ${f.gps_lat.toFixed(6)}, ${f.gps_lng.toFixed(6)} — otwórz w mapach`}
        >
          <MapPin size={11} /> geotag
        </a>
      ) : (
        <span
          className={geotagRequired ? CHIP_BAD : CHIP_WARN}
          title={
            geotagRequired
              ? "Ta pozycja wymaga zdjęć ze współrzędnymi GPS. Zdjęcie nie ma geotagu — sprawdź, czy telefon zapisuje lokalizację i czy plik nie przeszedł przez komunikator, który usuwa EXIF."
              : "Zdjęcie nie ma współrzędnych GPS w EXIF (komunikatory często je usuwają). Nie blokuje przesłania."
          }
        >
          <MapPinOff size={11} /> brak geotagu
        </span>
      )}
      {f.is_auto_rejected && (
        <span className={CHIP_BAD} title={reasons}>
          <MonitorX size={11} /> zrzut ekranu — odrzucono automatycznie
        </span>
      )}
      {f.was_restored && (
        <span className={CHIP_MUTED} title={reasons}>
          <RotateCcw size={11} /> odrzucone automatycznie, przywrócone przez{" "}
          {f.reviewed_by_name || "koordynatora"}
        </span>
      )}
      {f.photo_check === "screenshot" && !f.is_auto_rejected && !f.was_restored && (
        <span className={CHIP_WARN} title={reasons}>
          <MonitorX size={11} /> zrzut ekranu
        </span>
      )}
      {f.photo_check === "suspect" && (
        <span className={CHIP_WARN} title={`Do weryfikacji przy odbiorze: ${reasons}`}>
          <AlertTriangle size={11} /> do weryfikacji
        </span>
      )}
      {details && (
        <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
          <Camera size={11} /> {details}
        </span>
      )}
    </div>
  );
}

type PendingAction = { kind: "accept" | "reject" | "delete"; file: OrderResultFile } | null;

/**
 * Result-files block (pliki pozycji realizacyjnej). Self-contained: fetches the order's
 * overview and renders/acts based on the per-user capability flags returned by the API.
 *
 * - Contractor (can_upload): add files + download.
 * - Reviewer (can_manage): accept / reject / delete (modal-confirmed) + download.
 * - Everyone with access: download.
 *
 * Renders nothing when no line item expects result files (unless the contractor may upload).
 *
 * W „Widoku podwykonawcy" (podgląd GK) te flagi są ODWROTNE do tego, co widzi wykonawca:
 * overview leci z konta GK, więc `can_upload` jest zawsze false, a `can_manage` prawdziwe.
 * Podgląd pokazywał przez to przyciski odbioru, których podwykonawca nie ma, i ukrywał
 * pole przesyłania, które ma — a to czytało się jako „wykonawca nie może wgrać plików"
 * (zgłoszenie 2026-09-21). Prawo wgrywania po stronie API nie zależy od statusu zamówienia
 * ani od jego potwierdzenia, tylko od powiązania konta z firmą zamówienia, więc w podglądzie
 * pokazujemy pole wyłączone, zamiast udawać, że go nie ma.
 */
export function OrderResultFilesSection({ orderId }: { orderId: string }) {
  const { data, isLoading, isError } = useOrderResultFiles(orderId);
  const { isPreview } = useContractorPreview();
  const uploadMutation = useUploadResultFiles(orderId);
  const acceptMutation = useAcceptResultFile(orderId);
  const rejectMutation = useRejectResultFile(orderId);
  const deleteMutation = useDeleteResultFile(orderId);

  const [pending, setPending] = useState<Record<string, File[]>>({});
  const [uploadingItem, setUploadingItem] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [action, setAction] = useState<PendingAction>(null);
  const [rejectComment, setRejectComment] = useState("");
  const zipMutation = useMutation({
    mutationFn: () => resultFilesApi.downloadZip(orderId),
    onError: () => setError("Nie udało się pobrać plików jako ZIP."),
  });

  if (isLoading || isError || !data) return null;
  // Nothing flagged and the user cannot upload → render nothing.
  if (data.line_items.length === 0) return null;

  const can_manage = data.can_manage && !isPreview;
  const can_upload = data.can_upload;
  // Diagnoza dla zamawiającego: czy po stronie wykonawcy w ogóle JEST komu przesłać pliki.
  // Cisza, gdy wszystko gra — to podpowiedź pod zgłoszenie „podwykonawca nie widzi, gdzie
  // wgrać pliki", którego z konta GK nie dawało się rozstrzygnąć (can_upload niżej dotyczy
  // pytającego, więc u zamawiającego jest zawsze false).
  const accounts = data.contractor_accounts ?? [];
  const uploadBlockedHint =
    accounts.length === 0
      ? "Ta firma nie ma konta w portalu — nie ma kto przesłać plików. Konto zakłada się na karcie podwykonawcy, w sekcji „Konta użytkowników”."
      : accounts.every((a) => !a.can_upload)
        ? "Żadne konto portalu tej firmy nie może przesłać plików: konta są nieaktywne albo powiązane z inną firmą niż podwykonawca tego zamówienia."
        : "";
  // Bez ani jednego przesłanego pliku ZIP byłby pusty → przycisk pokazujemy dopiero, gdy jest co pakować.
  const totalFiles = data.line_items.reduce((sum, li) => sum + li.files.length, 0);

  const setItemFiles = (itemId: string, files: File[]) =>
    setPending((p) => ({ ...p, [itemId]: files }));

  const handleUpload = async (itemId: string) => {
    const files = pending[itemId] ?? [];
    if (!files.length) return;
    setError("");
    setUploadingItem(itemId);
    try {
      const uploaded = await uploadMutation.mutateAsync({ lineItemId: itemId, files });
      setItemFiles(itemId, []);
      const rejected = uploaded.filter((f) => f.is_auto_rejected);
      if (rejected.length > 0) {
        setError(
          `${rejected.length === 1 ? "Plik został odrzucony" : `${rejected.length} pliki zostały odrzucone`} ` +
            "automatycznie jako zrzut ekranu (nie fotografia): " +
            rejected.map((f) => f.original_name).join(", ") +
            ". Prześlij zdjęcia z aparatu lub telefonu — bez tego pozycji nie da się zgłosić do odbioru.",
        );
      }
    } catch {
      setError("Nie udało się przesłać plików. Maksymalny rozmiar pliku to 200 MB.");
    } finally {
      setUploadingItem(null);
    }
  };

  const confirmAction = async () => {
    if (!action) return;
    setError("");
    try {
      if (action.kind === "accept") await acceptMutation.mutateAsync(action.file.id);
      else if (action.kind === "reject")
        await rejectMutation.mutateAsync({ id: action.file.id, comment: rejectComment.trim() });
      else await deleteMutation.mutateAsync(action.file.id);
      setAction(null);
      setRejectComment("");
    } catch {
      setError("Nie udało się wykonać operacji. Spróbuj ponownie.");
    }
  };

  const actionPending =
    acceptMutation.isPending || rejectMutation.isPending || deleteMutation.isPending;

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 mb-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Pliki pozycji realizacyjnej
        </p>
        {totalFiles > 0 && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[var(--color-primary)] disabled:opacity-60 transition-colors"
            title="Pobierz wszystkie pliki wynikowe jako ZIP (folder na pozycję realizacyjną)"
            disabled={zipMutation.isPending}
            onClick={() => {
              setError("");
              zipMutation.mutate();
            }}
          >
            {zipMutation.isPending ? <Spinner size="sm" /> : <FolderArchive size={14} />}
            ZIP
          </button>
        )}
      </div>

      {error && <p className="mb-3 text-xs text-red-600 dark:text-red-400">{error}</p>}

      {can_manage && uploadBlockedHint && (
        <p className="mb-3 flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{uploadBlockedHint}</span>
        </p>
      )}

      <div className="flex flex-col gap-4">
        {data.line_items.map((item) => (
          <div
            key={item.id}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3"
          >
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <span className="flex flex-wrap items-baseline gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                {item.name}
                {item.geotag_required && (
                  <span
                    className={CHIP_MUTED}
                    title="Zdjęcia do tej pozycji powinny mieć współrzędne GPS (EXIF). Włącz zapis lokalizacji w aparacie i przesyłaj oryginalne pliki, nie kopie z komunikatora."
                  >
                    <MapPin size={11} /> wymagany geotag
                  </span>
                )}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {item.quantity} {item.unit}
              </span>
            </div>
            {item.unresolved_auto_rejected_ids.length > 0 && (
              <p className="mb-2 flex items-start gap-1.5 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
                <MonitorX size={14} className="mt-0.5 shrink-0" />
                <span>
                  Pozycja ma {item.unresolved_auto_rejected_ids.length === 1 ? "plik odrzucony" : "pliki odrzucone"}{" "}
                  automatycznie jako zrzut ekranu i nie może zostać zgłoszona do odbioru, dopóki
                  wykonawca nie prześle poprawnego zdjęcia
                  {can_manage ? " (albo koordynator nie przywróci pliku przyciskiem „Zaakceptuj”)" : ""}.
                </span>
              </p>
            )}

            {item.files.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {item.files.map((f) => (
                  <li
                    key={f.id}
                    className="flex flex-col gap-1 rounded-md border border-gray-100 dark:border-gray-700/60 px-2 py-1.5"
                  >
                  <div className="flex items-center gap-2">
                    <a
                      href={f.file_url ?? "#"}
                      download={f.original_name}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex min-w-0 flex-1 items-center gap-2"
                      title={`Pobierz ${f.original_name}`}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                        <FileText size={16} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-gray-800 dark:text-gray-100">
                          {f.original_name}
                        </span>
                        <span className="block text-xs text-gray-400 dark:text-gray-500">
                          {[f.uploaded_by_name, fmtDate(f.created_at), fmtSize(f.size_bytes)]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                      <Download
                        size={15}
                        className="shrink-0 text-gray-300 transition-colors group-hover:text-[var(--color-primary)]"
                      />
                    </a>

                    <StatusBadge status={f.status} />

                    {can_manage && (
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          title={f.is_auto_rejected ? "Przywróć plik odrzucony automatycznie (zaakceptuj)" : "Zaakceptuj"}
                          aria-label={`Zaakceptuj ${f.original_name}`}
                          disabled={f.status === "accepted"}
                          onClick={() => setAction({ kind: "accept", file: f })}
                          className="flex h-7 w-7 items-center justify-center rounded border border-gray-200 dark:border-gray-700 text-gray-400 hover:border-green-300 hover:text-green-600 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          type="button"
                          title="Odrzuć"
                          aria-label={`Odrzuć ${f.original_name}`}
                          disabled={f.status === "rejected"}
                          onClick={() => {
                            setRejectComment(f.review_comment ?? "");
                            setAction({ kind: "reject", file: f });
                          }}
                          className="flex h-7 w-7 items-center justify-center rounded border border-gray-200 dark:border-gray-700 text-gray-400 hover:border-amber-300 hover:text-amber-600 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <X size={14} />
                        </button>
                        <button
                          type="button"
                          title="Usuń"
                          aria-label={`Usuń ${f.original_name}`}
                          onClick={() => setAction({ kind: "delete", file: f })}
                          className="flex h-7 w-7 items-center justify-center rounded border border-gray-200 dark:border-gray-700 text-gray-400 hover:border-red-300 hover:text-red-600"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Outside the download anchor — nested <a> is invalid HTML. */}
                  <PhotoChips f={f} geotagRequired={item.geotag_required} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Brak przesłanych plików.
              </p>
            )}

            {/* Rejection reasons, visible to all who can read the files. */}
            {item.files.some((f) => f.status === "rejected" && f.review_comment) && (
              <ul className="mt-2 flex flex-col gap-1">
                {item.files
                  .filter((f) => f.status === "rejected" && f.review_comment)
                  .map((f) => (
                    <li key={`c-${f.id}`} className="text-xs text-red-600 dark:text-red-400">
                      <span className="font-medium">{f.original_name}:</span> {f.review_comment}
                    </li>
                  ))}
              </ul>
            )}

            {(can_upload || isPreview) && (
              <div className="mt-3 border-t border-gray-100 dark:border-gray-700/60 pt-3">
                <FileDropZone
                  files={pending[item.id] ?? []}
                  onFilesChange={(files) => setItemFiles(item.id, files)}
                  multiple
                  disabled={isPreview}
                  label={
                    isPreview
                      ? "Podgląd — tutaj podwykonawca przesyła pliki tej pozycji"
                      : undefined
                  }
                />
                {!isPreview && (pending[item.id]?.length ?? 0) > 0 && (
                  <div className="mt-2">
                    <Button
                      size="sm"
                      loading={uploadingItem === item.id && uploadMutation.isPending}
                      onClick={() => handleUpload(item.id)}
                    >
                      <Upload size={14} />{" "}
                      {(pending[item.id]?.length ?? 0) === 1
                        ? "Prześlij plik"
                        : `Prześlij pliki (${pending[item.id]?.length})`}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <Modal
        open={action !== null}
        onClose={() => {
          setAction(null);
          setRejectComment("");
        }}
        title={
          action?.kind === "accept"
            ? "Akceptacja pliku"
            : action?.kind === "reject"
              ? "Odrzucenie pliku"
              : "Usunięcie pliku"
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {action?.kind === "accept" && (
              <>
                Czy na pewno chcesz zaakceptować plik{" "}
                <span className="font-semibold">{action?.file.original_name}</span>?
                {action?.file.is_auto_rejected && (
                  <span className="mt-2 block text-xs text-amber-700 dark:text-amber-300">
                    Plik został odrzucony automatycznie jako zrzut ekranu
                    {action.file.photo_check_reasons?.length
                      ? ` (${action.file.photo_check_reasons.join("; ")})`
                      : ""}
                    . Akceptacja przywraca go z widocznym śladem „odrzucone automatycznie,
                    przywrócone przez Ciebie”.
                  </span>
                )}
              </>
            )}
            {action?.kind === "reject" && (
              <>
                Czy na pewno chcesz odrzucić plik{" "}
                <span className="font-semibold">{action?.file.original_name}</span>?
              </>
            )}
            {action?.kind === "delete" && (
              <>
                Czy na pewno chcesz usunąć plik{" "}
                <span className="font-semibold">{action?.file.original_name}</span>? Tej operacji
                nie można cofnąć.
              </>
            )}
          </p>

          {action?.kind === "reject" && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Powód odrzucenia (opcjonalnie)
              </label>
              <textarea
                rows={3}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Opcjonalny komentarz dla podwykonawcy…"
              />
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant={action?.kind === "accept" ? "primary" : "danger"}
              loading={actionPending}
              onClick={confirmAction}
            >
              {action?.kind === "accept"
                ? "Zaakceptuj"
                : action?.kind === "reject"
                  ? "Odrzuć"
                  : "Usuń"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setAction(null);
                setRejectComment("");
              }}
            >
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
